import { Loader } from "lucide-react";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import "./styles.scss";

interface InfiniteScrollerProps<
  T,
  P extends Record<string, any> = Record<string, any>
> {
  fetchFunction: (params: P) => Promise<{ data: T[]; count: number }>;
  initialParams: P;
  pageSize?: number;
  dependencyKey?: string | number | null;
  renderItem: (item: T, refresh: () => void, index: number) => React.ReactNode;
  direction?: "vertical" | "horizontal";
  className?: string;
  loadingComponent?: React.ReactNode;
  noItemsComponent?: React.ReactNode;
  onRefresh?: (refreshFunction: () => void) => void;
}

export const InfiniteScroller = <
  T,
  P extends Record<string, any> = Record<string, any>
>(
  props: InfiniteScrollerProps<T, P>
) => {
  // Destructure props
  const {
    fetchFunction,
    initialParams,
    pageSize = 10,
    dependencyKey = null,
    renderItem,
    direction = "vertical",
    className,
    loadingComponent,
    noItemsComponent,
    onRefresh,
  } = props;

  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`infiniteScroller.${key}`);

  // State management
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const loader = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<() => void>();
  const fetchFunctionRef = useRef(fetchFunction);
  const initialParamsRef = useRef(initialParams);

  // Update refs when props change
  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
    initialParamsRef.current = initialParams;
  }, [fetchFunction, initialParams]);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) {
      return;
    }

    setLoading(true);
    try {
      const currentOffset = offsetRef.current;

      // Fetch data with current parameters and offset
      const response = await fetchFunctionRef.current({
        ...initialParamsRef.current,
        offset: currentOffset,
        limit: pageSize,
      });

      const newItems = Array.isArray(response.data) ? response.data : [];
      const count = typeof response.count === "number" ? response.count : 0;

      if (newItems.length === 0) {
        setHasMore(false);
        return;
      }

      // Use Map to remove duplicate items
      setItems((prev) => {
        // Create a Map with item id as key to store existing items
        const existingItemsMap = new Map(
          prev.map((item: any) => [item.id, item])
        );

        // Filter out already existing items and merge the rest
        const uniqueNewItems = newItems.filter((item: any) => {
          return !existingItemsMap.has(item.id);
        });

        return [...prev, ...uniqueNewItems];
      });

      offsetRef.current = currentOffset + pageSize;

      // Determine if there are more items to load
      const totalLoaded = currentOffset + newItems.length;
      if (newItems.length < pageSize || (count > 0 && totalLoaded >= count)) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading items:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Initial loading
  useEffect(() => {
    // Reset state
    setItems([]);
    offsetRef.current = 0;
    setHasMore(true);
    hasMoreRef.current = true;

    // Initial load
    if (!loadingRef.current && loadMoreRef.current) {
      loadMoreRef.current();
    }
  }, [dependencyKey]); // Remove loadMore dependency

  // Track loading and hasMore states with refs
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Scroll handling
  useEffect(() => {
    if (direction === "vertical") {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
            loadMoreRef.current?.();
          }
        },
        { threshold: 0.1 }
      );
      const loaderEl = loader.current;
      if (loaderEl) {
        observer.observe(loaderEl);
      }
      return () => {
        if (loaderEl) {
          observer.unobserve(loaderEl);
        }
      };
    } else {
      const handleScroll = () => {
        const el = containerRef.current;
        if (!el || loadingRef.current || !hasMoreRef.current) return;

        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 100) {
          loadMoreRef.current?.();
        }
      };

      const el = containerRef.current;
      if (el) {
        el.addEventListener("scroll", handleScroll);

        if (
          el.scrollWidth <= el.clientWidth &&
          hasMoreRef.current &&
          !loadingRef.current
        ) {
          loadMoreRef.current?.();
        }
      }

      return () => {
        if (el) {
          el.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [hasMore, direction]); // Remove loadMore dependency

  const refresh = useCallback(() => {
    setItems([]);
    offsetRef.current = 0;
    setHasMore(true);
    hasMoreRef.current = true;
    if (loadMoreRef.current) {
      loadMoreRef.current();
    }
  }, []);

  // Register refresh function with parent component
  useEffect(() => {
    if (onRefresh) {
      onRefresh(refresh);
    }
  }, [onRefresh, refresh]);

  return (
    <div
      ref={containerRef}
      className={`infinite-scroller ${direction} ${className || ""}`}
    >
      <div
        className={
          direction === "horizontal" ? "horizontal-items-container" : undefined
        }
      >
        {items.length > 0 ? (
          items.map((item, idx) => renderItem(item, refresh, idx))
        ) : (
          <div className="no-items">
            {noItemsComponent || <div>{t("no_items")}</div>}
          </div>
        )}
      </div>

      {direction === "vertical" && hasMore && (
        <div ref={loader} style={{ height: 10 }} />
      )}

      {direction === "horizontal" && hasMore && (
        <div ref={loader} className="horizontal-loader" />
      )}

      {loading && (
        <div className="loading-posts">
          {loadingComponent || <Loader size={20} className="loading-icon" />}
        </div>
      )}
    </div>
  );
};
